'use client'

import type { StaticImageData } from 'next/image'

import { cn } from '@/utilities/ui'
import NextImage from 'next/image'
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'

import type { Props as MediaProps } from '../types'

import { getMediaUrl } from '@/utilities/getMediaUrl'
import { getFocalPointStyle } from '@/utilities/getFocalPointStyle'
import { getOptimizedImageProps } from '@inoo-ch/payload-image-optimizer/frontend'

export const ImageMedia: React.FC<MediaProps> = (props) => {
  const {
    alt: altFromProps,
    fill,
    pictureClassName,
    imgClassName,
    priority,
    fetchPriority,
    resource,
    size: sizeFromProps,
    src: srcFromProps,
    loading: loadingFromProps,
    disableBlur = false, // Default to false (blur enabled)
    onLoad,
  } = props

  // Track image load state for smooth fade-in transition
  const [imageLoaded, setImageLoaded] = useState(false)
  // Track the last loaded image to prevent unnecessary resets
  const lastLoadedImageRef = useRef<string | StaticImageData | null>(null)
  // Ref to container for cached image fallback check
  const containerRef = useRef<HTMLDivElement | HTMLElement>(null)
  // Stable ref for onLoad callback to avoid stale closures in effects
  const onLoadRef = useRef(onLoad)
  onLoadRef.current = onLoad

  // Memoize src calculation to prevent unnecessary re-renders and state resets
  const { src, width, height, alt, imageId } = useMemo(() => {
    let computedWidth: number | undefined
    let computedHeight: number | undefined
    let computedAlt = altFromProps
    let computedSrc: StaticImageData | string = srcFromProps || ''
    let computedImageId: string | null = null

    if (!computedSrc && resource && typeof resource === 'object') {
      const { alt: altFromResource, height: fullHeight, width: fullWidth, url, id } = resource

      computedWidth = fullWidth!
      computedHeight = fullHeight!
      computedAlt = altFromResource || ''
      computedImageId = id || null

      // Use PayloadCMS media URL with cache tag for cache busting
      const cacheTag = resource.updatedAt
      computedSrc = getMediaUrl(url, cacheTag)
    }

    return {
      src: computedSrc,
      width: computedWidth,
      height: computedHeight,
      alt: computedAlt,
      imageId: computedImageId,
    }
  }, [srcFromProps, resource, altFromProps])

  // Plugin returns:
  //   - blurDataURL: per-image ThumbHash for the placeholder layer
  //   - loader: variant-aware Image loader that serves pre-generated Payload
  //     size variants directly (skipping /_next/image) when a close-width
  //     match exists, falling back to /_next/image otherwise. Undefined when
  //     the media has no usable variants.
  const optimized = useMemo(() => {
    if (resource && typeof resource === 'object') {
      return getOptimizedImageProps(resource as any)
    }
    return null
  }, [resource])

  const thumbhash = useMemo(() => {
    if (optimized?.blurDataURL) return optimized.blurDataURL
    // Fallback to legacy thumbhash field during migration
    if (resource && typeof resource === 'object' && 'thumbhash' in resource && resource.thumbhash) {
      return resource.thumbhash as string
    }
    return undefined
  }, [optimized, resource])

  const loading = loadingFromProps || (!priority ? 'lazy' : undefined)

  // Get focal point style from resource if available
  const focalPointStyle =
    resource && typeof resource === 'object'
      ? getFocalPointStyle(resource.focalX, resource.focalY)
      : {}

  // Default to 100vw: over-request on grids (minor bandwidth) rather than
  // under-request on wide layouts (visible pixelation on 1x DPR displays).
  // Call sites with a narrower layout should pass an explicit `size`.
  const sizes = sizeFromProps ?? '100vw'

  // Reset loaded state only when image actually changes (by ID, not URL)
  // This prevents flicker when URL changes slightly but it's the same image
  useEffect(() => {
    // Determine the current image identifier (prefer ID over URL for stability)
    const currentImageId = imageId || (typeof src === 'string' ? src : null)

    // Only reset if this is a different image
    // If the image was already loaded and it's the same image, keep it loaded
    if (currentImageId && lastLoadedImageRef.current === currentImageId) {
      // Same image - check if it was already loaded
      // If it was loaded before, keep it loaded (don't reset)
      // This prevents flicker on re-renders
      return
    }

    // Different image or first load - update ref and reset loaded state
    lastLoadedImageRef.current = currentImageId
    setImageLoaded(false)
  }, [imageId, src])

  // Fallback for cached images: the load event can fire synchronously during
  // DOM insertion (before the reset useEffect above runs), causing the reset
  // to undo the loaded state. This effect re-checks after React settles.
  useEffect(() => {
    if (imageLoaded) return
    const img = containerRef.current?.querySelector('img')
    if (img?.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      setImageLoaded(true)
      onLoadRef.current?.()
    }
  }, [imageLoaded, imageId, src])

  // Handle image load - set loaded state ONLY when image is actually loaded
  // This ensures ThumbHash only disappears when the image is ready to display
  // onLoad fires for both cached and non-cached images in modern browsers
  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const img = e.currentTarget
      // Verify image is actually loaded and has dimensions
      // This ensures the image is ready to display before hiding ThumbHash
      if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
        setImageLoaded(true)
        onLoad?.()
      }
    },
    [onLoad],
  )

  // Merge styles: focal point only
  // NOTE: the image itself is always opacity 1 so Chrome considers it
  // LCP-eligible from first paint. Chrome ignores elements with opacity 0
  // for LCP, so fading in the <img> would defer the LCP timestamp by 500ms.
  // Instead we fade OUT the ThumbHash placeholder layered above.
  const imageStyle = {
    ...(focalPointStyle.objectPosition
      ? { objectPosition: `${focalPointStyle.objectPosition} !important` }
      : {}),
  }

  // Placeholder style - fades out as image loads
  const placeholderStyle = {
    transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: imageLoaded ? 0 : 1,
    ...(!imageLoaded ? { willChange: 'opacity' as const } : {}),
  }

  // When using fill, wrap in a container to ensure proper dimensions
  // Use absolute inset-0 to fill parent when parent is already positioned
  // This ensures Next.js Image's fill prop works correctly
  if (fill) {
    return (
      <div
        ref={containerRef as React.RefObject<HTMLDivElement>}
        className={cn(pictureClassName, 'absolute inset-0')}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        }}
      >
        {/* Actual image - always opacity 1 so Chrome picks it as LCP candidate */}
        <NextImage
          alt={alt || ''}
          className={cn(imgClassName, 'absolute inset-0 z-0')}
          fill
          priority={priority}
          fetchPriority={fetchPriority}
          quality={75}
          loading={loading}
          sizes={sizes}
          {...(optimized?.loader ? { loader: optimized.loader } : {})}
          src={src}
          onLoad={handleLoad}
          style={imageStyle}
        />
        {/* ThumbHash placeholder - layered above image, fades out on load */}
        {!disableBlur && thumbhash && (
          <div
            className="absolute inset-0 z-10 pointer-events-none"
            style={{
              backgroundImage: `url(${thumbhash})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              ...placeholderStyle,
            }}
            aria-hidden="true"
          />
        )}
      </div>
    )
  }

  // Non-fill mode - use picture element
  return (
    <picture ref={containerRef as React.RefObject<HTMLElement>} className={cn(pictureClassName, 'relative')}>
      {/* Actual image - always opacity 1 so Chrome picks it as LCP candidate */}
      <NextImage
        alt={alt || ''}
        className={cn(imgClassName, 'relative z-0')}
        height={height}
        priority={priority}
        fetchPriority={fetchPriority}
        quality={75}
        loading={loading}
        sizes={sizes}
        src={src}
        width={width}
        onLoad={handleLoad}
        style={imageStyle}
      />
      {/* ThumbHash placeholder - layered above image, fades out on load */}
      {!disableBlur && thumbhash && (
        <div
          className="absolute inset-0 z-10 pointer-events-none"
          style={{
            backgroundImage: `url(${thumbhash})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            ...placeholderStyle,
          }}
          aria-hidden="true"
        />
      )}
    </picture>
  )
}
